import React from "react";
// ANT DESIGN COMPONENTS
import { Button, Popconfirm, Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";

/**
 * One stored OPC UA row's card — same shell as `MappingsEditor`'s `MappingCard`:
 * a colored border, a two-line read (tags on top, raw reference below), Edit and
 * a confirmed Delete. Being edited, the summary stays visible (and follows the
 * draft, see the callers) with the form and Save/Cancel appended beneath it.
 * While a *different* row is being edited this card dims and its buttons
 * disable.
 */
export default function EditableRowCard({
  borderColor,
  tags,
  raw,
  editing,
  dimmed,
  canSave,
  deleteTitle,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  children,
}: {
  borderColor: string;
  tags: React.ReactNode;
  raw: React.ReactNode;
  editing: boolean;
  dimmed: boolean;
  canSave: boolean;
  deleteTitle: string;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid="editable-row"
      className="mb-2"
      style={{
        border: `2px solid ${borderColor}`,
        borderRadius: 6,
        marginTop: 5,
        padding: "8px 12px",
        filter: dimmed ? "grayscale(1)" : undefined,
        opacity: dimmed ? 0.55 : 1,
      }}
    >
      <div className="generalStyles__flex" style={{ alignItems: "center", gap: 8 }}>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="generalStyles__textEllipsis">{tags}</div>
          <div
            data-testid="row-summary"
            className="generalStyles__textEllipsis"
            style={{
              marginTop: 4,
              paddingTop: 10,
              fontFamily: "monospace",
              fontSize: 17,
              opacity: 0.85,
            }}
          >
            {raw}
          </div>
        </div>
        {!editing && (
          <>
            <div className="generalStyles__flexCenter" style={{ width: "50px", flexShrink: 0 }}>
              <Tooltip title="Edit">
                <Button
                  aria-label="Edit"
                  onClick={onEdit}
                  disabled={dimmed}
                  className="buttonStyle__29"
                  shape="circle"
                  icon={<FontAwesomeIcon icon={faPen} />}
                />
              </Tooltip>
            </div>
            <div className="generalStyles__flexCenter" style={{ width: "50px", flexShrink: 0 }}>
              <Popconfirm
                title={deleteTitle}
                onConfirm={onRemove}
                okText="Yes, Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                disabled={dimmed}
              >
                <Tooltip title="Delete">
                  <Button
                    aria-label="Delete"
                    type="primary"
                    danger
                    disabled={dimmed}
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </Popconfirm>
            </div>
          </>
        )}
      </div>
      {editing && (
        <div className="mt-2">
          {children}
          <div className="row mt-2">
            <div className="col-9">
              <Button
                onClick={onSave}
                disabled={!canSave}
                className={`generalStyles__width100 ${canSave ? "buttonStyle__28" : ""}`}
              >
                Save
              </Button>
            </div>
            <div className="col-3">
              <Button onClick={onCancel} danger className="generalStyles__width100">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
